/**
 * /sudo/templates — list of all editable notification templates.
 *
 * Each row is a (key, channel) pair. Click through to the edit view.
 * Read-only at this level; the edit view does the actual writes.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Mail, MessageSquare, Type } from 'lucide-react';
import { useDocumentTitle } from '../lib/use-document-title';

type Channel = 'email_subject' | 'email_body' | 'sms';

interface TemplateRow {
  key: string;
  label: string;
  channels: Array<{
    channel: Channel;
    present: boolean;
    bodyPreview: string | null;
    updatedAt: number | null;
  }>;
}

async function fetchTemplates(): Promise<{ templates: TemplateRow[] }> {
  const r = await fetch('/api/super/templates');
  if (!r.ok) throw new Error('list_failed');
  return r.json();
}

export const Route = createFileRoute('/sudo/templates/')({
  component: TemplatesList,
});

function TemplatesList() {
  useDocumentTitle('Templates — Super-admin');
  const listQ = useQuery({
    queryKey: ['sudo', 'templates'],
    queryFn: fetchTemplates,
  });

  return (
    <div>
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Notification templates</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Global defaults. Tenants on the Large business plan can override
          these from their own admin; tenants on lower plans see the
          editor disabled.
        </p>
      </header>

      {listQ.isLoading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
      ) : listQ.error ? (
        <p className="text-sm text-destructive">
          Couldn&rsquo;t load templates: {(listQ.error as Error).message}
        </p>
      ) : (
        <div className="space-y-6">
          {(listQ.data?.templates ?? []).map((row) => (
            <section
              key={row.key}
              className="rounded-2xl border border-brand-sage/15 bg-card p-5 shadow-brand"
            >
              <h2 className="text-base font-semibold">{row.label}</h2>
              <p className="text-xs text-muted-foreground">{row.key}</p>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {row.channels.map((c) => (
                  <Link
                    key={c.channel}
                    to="/sudo/templates/$key/$channel"
                    params={{ key: row.key, channel: c.channel }}
                    className="group flex flex-col rounded-lg border border-brand-sage/15 bg-background p-3 hover:border-primary/40 hover:bg-secondary/40"
                  >
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      <ChannelIcon channel={c.channel} />
                      <span>{channelLabel(c.channel)}</span>
                      <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-primary" />
                    </div>
                    <p className="mt-2 line-clamp-2 text-xs text-foreground">
                      {c.bodyPreview ?? <em className="text-muted-foreground">(no body)</em>}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ChannelIcon({ channel }: { channel: Channel }) {
  if (channel === 'email_subject') return <Type className="h-3.5 w-3.5" />;
  if (channel === 'email_body') return <Mail className="h-3.5 w-3.5" />;
  return <MessageSquare className="h-3.5 w-3.5" />;
}

function channelLabel(channel: Channel): string {
  if (channel === 'email_subject') return 'Email subject';
  if (channel === 'email_body') return 'Email body';
  return 'SMS';
}
